/**
 * Base Abstract SVGObject with transforms, opacity, filters, and lifecycle.
 */
import { Stage, StageLayerName } from '../core/Stage';
import { Renderer } from '../core/Renderer';
import { Bounds2D, Point2D } from '../core/Transform';

export interface SVGObjectConfig {
  id: string;
  type?: string;
  layer?: StageLayerName;
  x?: number;
  y?: number;
  scaleX?: number;
  scaleY?: number;
  rotation?: number; // in degrees
  opacity?: number;
  visible?: boolean;
  originX?: number;
  originY?: number;
  filter?: string;
  className?: string;
  appearTime?: number;
  disappearTime?: number;
  isInteractive?: boolean;
}

export abstract class SVGObject {
  public id: string;
  public type: string;
  public layer: StageLayerName;

  public x: number = 0;
  public y: number = 0;
  public scaleX: number = 1;
  public scaleY: number = 1;
  public rotation: number = 0; // degrees
  public opacity: number = 1;
  public visible: boolean = true;
  public originX: number = 0;
  public originY: number = 0;
  public filter: string = '';
  public appearTime: number = 0;
  public disappearTime: number = Infinity;
  public isInteractive: boolean = false;

  protected container: SVGGElement;
  protected stage: Stage | null = null;
  private isMounted: boolean = false;

  constructor(config: SVGObjectConfig) {
    this.id = config.id;
    this.type = config.type || 'generic';
    
    // Convert old numeric layers to named layers or fallback to 'objects'
    if (typeof config.layer === 'number') {
      this.layer = config.layer > 5 ? 'ui' : 'foreground';
    } else {
      this.layer = config.layer || 'objects';
    }

    this.x = config.x ?? 0;
    this.y = config.y ?? 0;
    this.scaleX = config.scaleX ?? 1;
    this.scaleY = config.scaleY ?? 1;
    this.rotation = config.rotation ?? 0;
    this.opacity = config.opacity ?? 1;
    this.visible = config.visible ?? true;
    this.originX = config.originX ?? 0;
    this.originY = config.originY ?? 0;
    this.filter = config.filter ?? '';
    this.appearTime = config.appearTime ?? 0;
    this.disappearTime = config.disappearTime ?? Infinity;

    this.container = Renderer.createElement('g');
    this.container.setAttribute('id', `obj-${this.id}`);
    this.container.classList.add('asae-object', `obj-type-${this.type}`);
    if (config.className) {
      this.container.classList.add(config.className);
    }

    this.isInteractive = config.isInteractive ?? false;
    if (this.isInteractive) {
      this.setupInteractions();
    } else {
      this.container.style.cursor = 'default';
    }
  }

  public setInteractive(interactive: boolean): void {
    this.isInteractive = interactive;
    if (!interactive) {
      this.container.style.cursor = 'default';
      this.container.onpointerdown = null;
      this.hideSelectionOutline();
      this.setSelected(false);
    } else {
      this.setupInteractions();
    }
  }

  public isSelected: boolean = false;
  public onSelect?: (objectId: string) => void;
  public onChange?: (data: { id: string; x: number; y: number; isDragging?: boolean }) => void;

  public setSelected(selected: boolean): void {
    this.isSelected = selected;
    if (selected && this.isInteractive) {
      this.container.classList.add('selected');
      this.showSelectionOutline();
    } else {
      this.container.classList.remove('selected');
      this.hideSelectionOutline();
    }
  }

  private showSelectionOutline(): void {
    if (!this.isInteractive) return;
    let outline = this.container.querySelector('.asae-selection-outline') as SVGElement;
    if (!outline) {
      outline = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      outline.setAttribute('class', 'asae-selection-outline');
      outline.setAttribute('fill', 'none');
      outline.setAttribute('stroke', '#38bdf8');
      outline.setAttribute('stroke-width', '2');
      outline.setAttribute('stroke-dasharray', '5,5');
      outline.setAttribute('pointer-events', 'none');
      this.container.appendChild(outline);
    }
    try {
      const bbox = (this.container as any).getBBox?.() || { x: -50, y: -50, width: 100, height: 100 };
      outline.setAttribute('x', String(bbox.x - 6));
      outline.setAttribute('y', String(bbox.y - 6));
      outline.setAttribute('width', String(bbox.width + 12));
      outline.setAttribute('height', String(bbox.height + 12));
      outline.style.display = 'block';
    } catch {
      outline.style.display = 'none';
    }
  }

  private hideSelectionOutline(): void {
    const outline = this.container.querySelector('.asae-selection-outline') as SVGElement;
    if (outline) {
      outline.style.display = 'none';
    }
  }

  protected setupInteractions(): void {
    if (!this.isInteractive) {
      this.container.style.cursor = 'default';
      this.container.onpointerdown = null;
      return;
    }

    this.container.style.cursor = 'move';

    let isPointerDown = false;
    let startPointerX = 0;
    let startPointerY = 0;
    let startObjX = 0;
    let startObjY = 0;

    this.container.onpointerdown = (e: PointerEvent) => {
      if (!this.isInteractive) return;
      e.stopPropagation();
      isPointerDown = true;
      startPointerX = e.clientX;
      startPointerY = e.clientY;
      startObjX = this.x;
      startObjY = this.y;

      this.onSelect?.(this.id);

      const onPointerMove = (moveEvt: PointerEvent) => {
        if (!isPointerDown || !this.isInteractive) return;
        
        let scale = 1;
        if (this.stage) {
          const svgEl = this.stage.getSVG();
          const rect = svgEl.getBoundingClientRect();
          const dims = this.stage.getBaseDimensions();
          if (rect.width > 0) {
            scale = dims.width / rect.width;
          }
        }

        const deltaX = (moveEvt.clientX - startPointerX) * scale;
        const deltaY = (moveEvt.clientY - startPointerY) * scale;

        this.x = Math.round(startObjX + deltaX);
        this.y = Math.round(startObjY + deltaY);

        this.applyTransform();
        this.showSelectionOutline();
        this.onChange?.({ id: this.id, x: this.x, y: this.y, isDragging: true });
      };

      const onPointerUp = () => {
        if (!isPointerDown) return;
        isPointerDown = false;
        window.removeEventListener('pointermove', onPointerMove);
        window.removeEventListener('pointerup', onPointerUp);
        this.onChange?.({ id: this.id, x: this.x, y: this.y, isDragging: false });
      };

      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', onPointerUp);
    };
  }

  public getContainer(): SVGGElement {
    return this.container;
  }

  public mount(stage: Stage): void {
    this.stage = stage;
    const targetLayer = stage.getLayer(this.layer);
    if (targetLayer && !this.container.parentNode) {
      targetLayer.appendChild(this.container);
      this.isMounted = true;
      this.render();
      this.applyTransform();
      if (this.isSelected) {
        this.showSelectionOutline();
      }
    }
  }

  public unmount(): void {
    if (this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }
    this.isMounted = false;
    this.stage = null;
  }

  public abstract render(): void;

  public update(time: number, delta: number): void {
    if (!this.isMounted) return;
    this.applyTransform();
  }

  public applyTransform(): void {
    let tf = `translate(${this.x.toFixed(2)}, ${this.y.toFixed(2)})`;
    if (this.rotation !== 0) {
      tf += ` rotate(${this.rotation.toFixed(2)}, ${this.originX.toFixed(2)}, ${this.originY.toFixed(2)})`;
    }
    if (this.scaleX !== 1 || this.scaleY !== 1) {
      tf += ` scale(${this.scaleX.toFixed(4)}, ${this.scaleY.toFixed(4)})`;
    }

    Renderer.setAttributes(this.container, {
      transform: tf,
      opacity: this.opacity.toFixed(3),
      display: this.visible ? 'block' : 'none',
      filter: this.filter ? `url(#${this.filter})` : null
    });
  }

  public getBounds(): Bounds2D {
    try {
      if (this.isMounted && this.container.getBBox) {
        const box = this.container.getBBox();
        return {
          x: this.x + box.x,
          y: this.y + box.y,
          width: box.width * this.scaleX,
          height: box.height * this.scaleY
        };
      }
    } catch {
      // Fallback
    }
    return { x: this.x, y: this.y, width: 100, height: 100 };
  }

  public getCenter(): Point2D {
    const b = this.getBounds();
    return { x: b.x + b.width / 2, y: b.y + b.height / 2 };
  }

  public destroy(): void {
    this.unmount();
    this.container.innerHTML = '';
  }
}
